import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import NovelCard from "@/components/feature/NovelCard";
import NewsListItem from "@/components/feature/NewsListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { allMovies, searchCatalog } from "@/utils/catalog";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const TABS = [
  { key: "all", label: "全部" },
  { key: "movie", label: "影视" },
  { key: "novel", label: "小说" },
  { key: "news", label: "资讯" },
];

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") || "";
  const [input, setInput] = useState(urlKeyword);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);

  useDocumentTitle(
    urlKeyword ? `“${urlKeyword}”的搜索结果 - 17C.COM` : "内容搜索 - 17C.COM",
    "在 17C.COM 搜索影视、小说与娱乐资讯内容。"
  );

  const result = useMemo(() => searchCatalog(urlKeyword), [urlKeyword]);

  const activeList = useMemo(() => {
    if (tab === "movie") return result.movies;
    if (tab === "novel") return result.novels;
    if (tab === "news") return result.news;
    return [];
  }, [tab, result]);

  const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = activeList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setSearchParams(kw ? { q: kw } : {});
    setPage(1);
    setTab("all");
  };

  const tabCount = (key: string) => {
    if (key === "movie") return result.movies.length;
    if (key === "novel") return result.novels.length;
    if (key === "news") return result.news.length;
    return result.total;
  };

  const hasKeyword = urlKeyword.trim().length > 0;

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 pb-2">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
      </div>

      <div className="w-full px-4 md:px-8 pt-4 pb-10 md:pb-14">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-4">
            内容搜索
          </h1>
          <form
            onSubmit={submit}
            action="/search"
            method="get"
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3"
          >
            <div className="flex items-center gap-2 h-12 px-4 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors flex-1">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、小说书名、作者或资讯关键词"
                className="flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex items-center gap-2 flex-wrap text-sm">
            <span className="text-foreground-500">热门搜索：</span>
            {["星河之下", "玄幻", "悬疑", "都市迷雾", "剑起长歌", "明星资讯"].map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => {
                  setInput(kw);
                  setSearchParams({ q: kw });
                  setPage(1);
                  setTab("all");
                }}
                className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>

        {hasKeyword && (
          <div className="mt-6">
            <p className="text-sm text-foreground-600 mb-4">
              关键词 “<span className="font-medium text-primary-600">{urlKeyword}</span>” 共找到
              <span className="font-medium text-primary-600"> {result.total} </span>条结果
            </p>

            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-full sm:w-fit mb-6">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    setTab(t.key);
                    setPage(1);
                  }}
                  className={`flex-1 sm:flex-none px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    tab === t.key
                      ? "bg-background-50 text-primary-600 border border-primary-200"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {t.label}
                  <span className="ml-1 text-xs text-foreground-400">{tabCount(t.key)}</span>
                </button>
              ))}
            </div>

            {result.total === 0 ? (
              <div>
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到相关内容"
                  description={`未找到与 “${urlKeyword}” 匹配的影视、小说或资讯，换个关键词试试吧。`}
                />
                <div className="mt-2">
                  <h3 className="font-heading text-lg font-black text-foreground-950 mb-4">热门影视推荐</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
                    {[...allMovies].sort((a, b) => b.heat - a.heat).slice(0, 6).map((m) => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </div>
              </div>
            ) : tab === "all" ? (
              <div className="flex flex-col gap-10">
                {result.movies.length > 0 && (
                  <section>
                    <h3 className="font-heading text-lg font-black text-foreground-950 mb-4">
                      影视 · {result.movies.length} 条
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
                      {result.movies.slice(0, 6).map((m) => (
                        <MovieCard key={m.id} movie={m} />
                      ))}
                    </div>
                  </section>
                )}
                {result.novels.length > 0 && (
                  <section>
                    <h3 className="font-heading text-lg font-black text-foreground-950 mb-4">
                      小说 · {result.novels.length} 条
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                      {result.novels.slice(0, 4).map((n) => (
                        <NovelCard key={n.id} novel={n} />
                      ))}
                    </div>
                  </section>
                )}
                {result.news.length > 0 && (
                  <section>
                    <h3 className="font-heading text-lg font-black text-foreground-950 mb-4">
                      资讯 · {result.news.length} 条
                    </h3>
                    <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                      {result.news.slice(0, 5).map((n) => (
                        <NewsListItem key={n.id} item={n} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <>
                {paged.length > 0 ? (
                  <>
                    {tab === "movie" && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
                        {result.movies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((m) => (
                          <MovieCard key={m.id} movie={m} />
                        ))}
                      </div>
                    )}
                    {tab === "novel" && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                        {result.novels.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((n) => (
                          <NovelCard key={n.id} novel={n} />
                        ))}
                      </div>
                    )}
                    {tab === "news" && (
                      <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                        {result.news.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((n) => (
                          <NewsListItem key={n.id} item={n} />
                        ))}
                      </div>
                    )}
                    <div className="mt-10">
                      <Pagination current={currentPage} total={totalPages} onChange={setPage} />
                    </div>
                  </>
                ) : (
                  <EmptyState
                    icon="ri-search-eye-line"
                    title="该分类下没有匹配结果"
                    description={`“${urlKeyword}” 在「${TABS.find((t) => t.key === tab)?.label}」分类下没有匹配内容，试试其他分类。`}
                  />
                )}
              </>
            )}
          </div>
        )}

        {!hasKeyword && (
          <div className="mt-8">
            <h3 className="font-heading text-lg font-black text-foreground-950 mb-4">热门影视推荐</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {[...allMovies].sort((a, b) => b.heat - a.heat).slice(0, 6).map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}